import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { registerUser } from '../components/DbServices';
import AuthLayout from '../components/AuthLayout';
import CryptoJS from "crypto-js";
import Swal from 'sweetalert2';

export default function Register() {
  const navigate = useNavigate();

  // Form States
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  
  // UI Logic States
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const submit = async (e) => {
    if (e) e.preventDefault();
    setError('');

    if (!name || !email || !password) {
      setError('Please fill in all fields to continue');
      return;
    }

    try {
      setLoading(true);
      // Sending request to your CodeIgniter backend
      const encryptedPassword = CryptoJS.SHA256(password).toString();
      const response = await registerUser({ name, email, password :encryptedPassword});

      if (response?.status === 200) {
        // Success: Don't navigate yet, show the "Check Email" view
        setIsSubmitted(true);
      } else {
        // Handle backend error messages (e.g., "Email already registered")
        setError(response?.data?.message || response?.message || 'Registration failed');
      }
    } catch (err) {
      setError('Connection error. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * SUCCESS STATE: Shown after the backend returns status 200
   */
  if (isSubmitted) {
    return (
      <AuthLayout 
        title="Check your inbox" 
        subtitle="Account created successfully"
      >
        <div className="text-center py-4 animate__animated animate__fadeIn">
          <div className="mb-4 position-relative d-inline-block">
            <div className="rounded-circle bg-primary bg-opacity-10 d-flex align-items-center justify-content-center pulse-container" 
                 style={{ width: '100px', height: '100px' }}>
              <i className="fas fa-envelope-open-text text-primary" style={{ fontSize: '2.5rem' }}></i>
            </div>
            <div className="position-absolute bottom-0 end-0 bg-success rounded-circle border border-white border-3" 
                 style={{ width: '25px', height: '25px' }}></div>
          </div>

          <h4 className="fw-black text-dark mb-3">Verify your email</h4>
          <p className="text-muted mb-5 px-3">
            We've sent a verification link to <br />
            <strong className="text-dark">{email}</strong>. <br />
            Please click the link in that email to activate your account.
          </p>

          <Link to="/login" className="btn btn-primary w-100 rounded-pill py-3 fw-black shadow-sm">
            PROCEED TO LOGIN <i className="fas fa-arrow-right ms-2"></i>
          </Link>

          <p className="mt-4 small text-muted">
            Didn't receive the email? <span className="text-primary fw-bold" style={{ cursor: 'pointer' }} onClick={() => setIsSubmitted(false)}>Try again</span>
          </p>
        </div>

        <style>{`
          .pulse-container {
            animation: softPulse 2s infinite;
          }
          @keyframes softPulse {
            0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(76, 29, 149, 0.2); }
            70% { transform: scale(1.05); box-shadow: 0 0 0 15px rgba(76, 29, 149, 0); }
            100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(76, 29, 149, 0); }
          }
        `}</style>
      </AuthLayout>
    );
  }

  /**
   * DEFAULT STATE: The Registration Form
   */
  return (
    <AuthLayout
      title="Create account"
      subtitle="Join thousands of businesses using our API"
    >
      {error && (
        <div className="Swal Swal-danger border-0 small fw-bold py-3 rounded-4 mb-4 animate__animated animate__shakeX">
          <i className="fas fa-exclamation-circle me-2"></i> {error}
        </div>
      )}

      <form onSubmit={submit}>
        {/* FULL NAME */}
        <div className="mb-3">
          <label className="form-label small fw-black text-muted mb-2 tracking-wider">FULL NAME</label>
          <div className="input-group">
            <span className="input-group-text bg-light border-light-subtle rounded-start-3 px-3">
              <i className="fas fa-user text-muted small"></i>
            </span>
            <input
              type="text"
              className="form-control bg-light border-light-subtle py-3 rounded-end-3 shadow-none"
              placeholder="e.g. John Doe"
              style={{ fontSize: '14px' }}
              value={name}
              onChange={e => setName(e.target.value)}
              required
            />
          </div>
        </div>

        {/* EMAIL ADDRESS */}
        <div className="mb-3">
          <label className="form-label small fw-black text-muted mb-2 tracking-wider">EMAIL ADDRESS</label>
          <div className="input-group">
            <span className="input-group-text bg-light border-light-subtle rounded-start-3 px-3">
              <i className="fas fa-envelope text-muted small"></i>
            </span>
            <input
              type="email"
              className="form-control bg-light border-light-subtle py-3 rounded-end-3 shadow-none"
              placeholder="name@company.com"
              style={{ fontSize: '14px' }}
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
            />
          </div>
        </div>

        {/* PASSWORD */}
        <div className="mb-4">
          <label className="form-label small fw-black text-muted mb-2 tracking-wider">CHOOSE PASSWORD</label>
          <div className="input-group">
            <span className="input-group-text bg-light border-light-subtle rounded-start-3 px-3">
              <i className="fas fa-lock text-muted small"></i>
            </span>
            <input
              type="password"
              className="form-control bg-light border-light-subtle py-3 rounded-end-3 shadow-none"
              placeholder="Min. 8 characters"
              style={{ fontSize: '14px' }}
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
            />
          </div>
        </div>

        {/* SUBMIT BUTTON */}
        <button
          className="btn w-100 py-3 rounded-pill fw-black text-white shadow-lg border-0 transition-all hover-lift"
          style={{ 
            background: 'linear-gradient(135deg, #4C1D95 0%, #0E7490 100%)',
            letterSpacing: '1px'
          }}
          disabled={loading}
          type="submit"
        >
          {loading ? (
            <span className="spinner-border spinner-border-sm me-2"></span>
          ) : (
            <>CREATE MY ACCOUNT <i className="fas fa-rocket ms-2"></i></>
          )}
        </button>
      </form>

      {/* FOOTER */}
      <div className="text-center mt-4">
        <p className="text-muted small fw-medium">
          Already have an account? <Link to="/login" className="text-primary fw-black text-decoration-none">Sign in</Link>
        </p>
      </div>

      <style>{`
        .hover-lift:hover { transform: translateY(-2px); filter: brightness(1.1); }
        .fw-black { font-weight: 900 !important; }
        .tracking-wider { letter-spacing: 0.05rem; }
      `}</style>
    </AuthLayout>
  );
}